import { mapGetters, mapActions } from 'vuex';
import PcRenderPromo from '@garmin/components-product/src/components/PcRenderPromo';
import ImageGalleryCarousel from '@/components/ImageGalleryCarousel/index.vue';
import GallerySlider from '@/components/GallerySlider/index.vue';
import breakpoints from '../../helpers/breakpoints';
import loadImages from '../../helpers/preloadImages';

export default {
  name: 'Gallery',
  data() {
    return {
      className: 'app__product__images',
      galleryMode: null,
    };
  },
  props: {
    showDesktopBanner: {
      type: Boolean,
    },
  },
  mounted() {
    // Register an event listener when the Vue component is ready
    this.galleryMode = this.calculateGalleryMode(window);
    window.addEventListener('resize', this.onResize);
  },
  beforeUpdate() {
    this.galleryMode = this.calculateGalleryMode(window);
  },
  beforeDestroy() {
    // Unregister the event listener before destroying this Vue instance
    window.removeEventListener('resize', this.onResize);
  },
  computed: {
    ...mapGetters({
      productId: 'getProductId',
      partNumber: 'getPartNumber',
      buyGarminEndpoint: 'getBuyGarminEndpoint',
      locale: 'getLocale',
      storeCode: 'getStoreCode',
      galleryImageList: 'getGalleryImageList',
      activeImageIndex: 'getActiveImageIndex',
      isMultiImageGallery: 'getIsMultiImageGallery',
      galleryLoaded: 'getGalleryLoaded',
      spinnerThumbnail: 'getSpinnerThumbnailBySku',
      productSpinnerImages: 'getProductSpinnerImagesBySku',
      loadedSpinnerImages: 'getLoadedSpinnerImages',
      promoBanner: 'getPromoBanner',
      interstitialIsVisible: 'getInterstitialIsVisible',
      startedPreloading: 'getStartedPreloadingBySku',
    }),
    jsGalleryConId() {
      return 'js__product__gallery__con';
    },
    showGallerySlider() {
      return this.galleryMode && this.galleryLoaded && !this.interstitialIsVisible;
    },
    getImagesWithSpinner() {
      const insert = (arr, index, ...newItems) => [
        ...arr.slice(0, index),
        ...newItems,
        ...arr.slice(index),
      ];
      const sku = this.partNumber;
      const thumbnail = this.spinnerThumbnail(sku);

      if (thumbnail) {
        if (this.galleryImageList.length) {
          const imagesWithSpinner = insert(this.galleryImageList, 1, thumbnail);
          return imagesWithSpinner;
        }
        return [thumbnail];
      }
      return this.galleryImageList;
    },
    showSpinnerLink() {
      const activeImage = this.getImagesWithSpinner[this.activeImageIndex];
      return activeImage && activeImage.type === '360';
    },
    imageViewerLink() {
      return `${this.buyGarminEndpoint}/${this.locale}/${this.storeCode}/p/${this.productId}/pn/${this.partNumber}/imageviewer`;
    },
  },
  components: {
    ImageGalleryCarousel,
    GallerySlider,
    PcRenderPromo,
  },
  methods: {
    calculateGalleryMode(target) {
      // No gallery slider if only 1 image
      if (this.getImagesWithSpinner.length <= 1) return null;

      const width = target.innerWidth;
      // If lower than desktop-s don't show the slider at all
      if (width < breakpoints['desktop-s']) return null;
      return width < breakpoints['desktop-m'] ? 'small' : 'big';
    },
    onResize(event) {
      const { gallerySlider } = this.$refs;
      this.galleryMode = this.calculateGalleryMode(event.target);
      if (gallerySlider) gallerySlider.updateSliderHeight();
    },
    preloadImages() {
      const sku = this.partNumber;

      if (!this.startedPreloading(sku)) {
        this.setStartedPreloading({ sku, value: true });
        const images = this.productSpinnerImages(sku) || [];
        const loadedImages = this.loadedSpinnerImages[sku];
        const promises = [];

        if (loadedImages || !images.length) {
          return;
        }

        // Preload the images one by one
        images.forEach((src) => {
          promises.push(loadImages(src, null, this.removeBrokenImage));
        });

        // After all are done set them as loaded in the store
        Promise.all(promises).then(() => {
          this.setLoadedSpinnerImages({
            sku,
            value: true,
          });
        });
      }
    },
    openViewer(e) {
      e.preventDefault();
      this.$router.push({
        name: 'ViewerPath',
        params: {
          ...this.$router.currentRoute.params,
          // Include the part number so the PID route also works
          partNumber: this.partNumber,
        },
        hash: this.$router.currentRoute.hash,
      });
    },
    ...mapActions([
      'updateActiveImageIndex',
      'setLoadedSpinnerImages',
      'removeBrokenImage',
      'setStartedPreloading',
    ]),
  },
};
